/**
 * Dashboard Type Utility Functions
 *
 * Centralizes all dashboard type handling to ensure consistent format
 * across the application. Backend normalizes to 'Algo.XX' format.
 *
 * @module utils/dashboardTypeUtils
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Canonical dashboard type values (matching backend normalized format)
 */
export const DASHBOARD_TYPES = {
    LEGACY: 'Algo.01',
    ANALYTICS: 'Algo.02',
    ADVANCED: 'Algo.03',
    EXPERT: 'Algo.04'
};

/**
 * Display names for UI
 */
export const DASHBOARD_DISPLAY_NAMES = {
    'Algo.01': 'Legacy Dashboard',
    'Algo.02': 'Analytics Dashboard',
    'Algo.03': 'Advanced Dashboard',
    'Algo.04': 'Expert Dashboard'
};

// ============================================================================
// NORMALIZATION
// ============================================================================

/**
 * Normalizes any dashboard type format to canonical 'Algo.XX' format
 *
 * Handles all known variations:
 * - Dot format: 'Algo.01', 'Algo.1'
 * - Hyphen format: 'Algo-1', 'Algo-01'
 * - No separator: 'Algo1', 'Algo01'
 * - Case insensitive
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {string} Normalized dashboard type (defaults to 'Algo.01')
 *
 * @example
 * normalizeDashboardType('Algo-1')   // 'Algo.01'
 * normalizeDashboardType('Algo.02')  // 'Algo.02'
 * normalizeDashboardType('algo2')    // 'Algo.02'
 * normalizeDashboardType(null)       // 'Algo.01'
 */
export const normalizeDashboardType = (type) => {
    // Default to Legacy if null/undefined/empty
    if (!type || typeof type !== 'string') {
        return DASHBOARD_TYPES.LEGACY;
    }

    // Normalize: lowercase, remove separators
    const normalized = type.toLowerCase().replace(/[.\-_]/g, '');

    // Map all variations to canonical format
    const typeMap = {
        // Legacy Dashboard variations
        'algo1': DASHBOARD_TYPES.LEGACY,
        'algo01': DASHBOARD_TYPES.LEGACY,

        // Analytics Dashboard variations
        'algo2': DASHBOARD_TYPES.ANALYTICS,
        'algo02': DASHBOARD_TYPES.ANALYTICS,

        // Advanced Dashboard variations
        'algo3': DASHBOARD_TYPES.ADVANCED,
        'algo03': DASHBOARD_TYPES.ADVANCED,

        // Expert Dashboard variations
        'algo4': DASHBOARD_TYPES.EXPERT,
        'algo04': DASHBOARD_TYPES.EXPERT
    };

    return typeMap[normalized] || DASHBOARD_TYPES.LEGACY;
};

// ============================================================================
// TYPE CHECKERS
// ============================================================================

/**
 * Checks if dashboard type is Legacy (Algo.01)
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {boolean} True if Legacy dashboard
 *
 * @example
 * isLegacyDashboard('Algo.01')  // true
 * isLegacyDashboard('Algo-1')   // true
 * isLegacyDashboard('Algo.02')  // false
 * isLegacyDashboard(null)       // true (default)
 */
export const isLegacyDashboard = (type) => {
    return normalizeDashboardType(type) === DASHBOARD_TYPES.LEGACY;
};

/**
 * Checks if dashboard type is Analytics (Algo.02)
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {boolean} True if Analytics dashboard
 *
 * @example
 * isAnalyticsDashboard('Algo.02')  // true
 * isAnalyticsDashboard('Algo-2')   // true
 * isAnalyticsDashboard('Algo.01')  // false
 */
export const isAnalyticsDashboard = (type) => {
    return normalizeDashboardType(type) === DASHBOARD_TYPES.ANALYTICS;
};

/**
 * Checks if dashboard type is Advanced (Algo.03)
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {boolean} True if Advanced dashboard
 */
export const isAdvancedDashboard = (type) => {
    return normalizeDashboardType(type) === DASHBOARD_TYPES.ADVANCED;
};

/**
 * Checks if dashboard type is Expert (Algo.04)
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {boolean} True if Expert dashboard
 */
export const isExpertDashboard = (type) => {
    return normalizeDashboardType(type) === DASHBOARD_TYPES.EXPERT;
};

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Gets human-readable display name for dashboard type
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {string} Display name for UI
 *
 * @example
 * getDashboardDisplayName('Algo.02')  // 'Analytics Dashboard'
 * getDashboardDisplayName('Algo-1')   // 'Legacy Dashboard'
 */
export const getDashboardDisplayName = (type) => {
    const normalized = normalizeDashboardType(type);
    return DASHBOARD_DISPLAY_NAMES[normalized] || 'Unknown Dashboard';
};

/**
 * Gets short label for dashboard type (for badges, chips)
 *
 * @param {string|null|undefined} type - Dashboard type in any format
 * @returns {string} Short label (e.g., 'Algo.01')
 */
export const getDashboardShortLabel = (type) => {
    return normalizeDashboardType(type);
};

// ============================================================================
// COMPARISON HELPERS
// ============================================================================

/**
 * Compares two dashboard types for equality (format-agnostic)
 *
 * @param {string|null|undefined} type1 - First dashboard type
 * @param {string|null|undefined} type2 - Second dashboard type
 * @returns {boolean} True if both resolve to same dashboard
 *
 * @example
 * dashboardTypesMatch('Algo.01', 'Algo-1')  // true
 * dashboardTypesMatch('Algo.01', 'Algo.02') // false
 */
export const dashboardTypesMatch = (type1, type2) => {
    return normalizeDashboardType(type1) === normalizeDashboardType(type2);
};

/**
 * Checks if a dashboard type matches the target dashboard
 *
 * @param {string|null|undefined} type - Dashboard type to check
 * @param {string} targetDashboard - Target from DASHBOARD_TYPES constant
 * @returns {boolean} True if matches
 *
 * @example
 * matchesDashboard('Algo-2', DASHBOARD_TYPES.ANALYTICS)  // true
 */
export const matchesDashboard = (type, targetDashboard) => {
    return normalizeDashboardType(type) === targetDashboard;
};

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validates if a string is a recognized dashboard type
 *
 * @param {string|null|undefined} type - Value to validate
 * @returns {boolean} True if valid dashboard type
 */
export const isValidDashboardType = (type) => {
    if (!type || typeof type !== 'string') {
        return false;
    }

    const normalized = normalizeDashboardType(type);
    return Object.values(DASHBOARD_TYPES).includes(normalized);
};

// ============================================================================
// EXPORTS SUMMARY
// ============================================================================

export default {
    // Constants
    DASHBOARD_TYPES,
    DASHBOARD_DISPLAY_NAMES,

    // Normalization
    normalizeDashboardType,

    // Type checkers
    isLegacyDashboard,
    isAnalyticsDashboard,
    isAdvancedDashboard,
    isExpertDashboard,

    // Display helpers
    getDashboardDisplayName,
    getDashboardShortLabel,

    // Comparison
    dashboardTypesMatch,
    matchesDashboard,

    // Validation
    isValidDashboardType
};
